<template>
  <!-- Bottom-of-nav account row (studio-design settings sidebar footer): avatar
       initials + name + chevron, opens the account menu (View profile / Sign
       out). The menu floats anchored above the row (to the right when the rail
       is collapsed), so it isn't clipped by the nav shell. -->
  <div class="border-t border-surface-200 pt-2">
    <AppMenuNew
      :placement="collapsed ? 'right-end' : 'top-start'"
      :offset="8"
    >
      <template #trigger="{ toggleMenu }">
        <button
          v-tooltip.right="collapsed ? displayName : undefined"
          class="flex w-full items-center gap-2.5 rounded-lg py-2 text-surface-800 hover:bg-surface-100 transition-colors"
          :class="collapsed ? 'justify-center px-0' : 'px-2'"
          data-testid="nav-account-button"
          @click="toggleMenu"
        >
          <AppProfileAvatar
            :variant="avatarUrl ? 'avatar' : 'initials'"
            :url="userAvatarSrc(avatarUrl, 128)"
            :initials="initials"
            :element-size="28"
          />
          <template v-if="!collapsed">
            <span class="flex-1 truncate text-left text-sm font-medium">{{
              displayName
            }}</span>
            <AppIcon
              icon="fa-solid fa-chevron-up"
              class="text-2xs text-surface-500 shrink-0"
            />
          </template>
        </button>
      </template>

      <template #items>
        <AppMenuNewItem
          v-if="profileTo"
          icon="fa-regular fa-user"
          label="View profile"
          :to="profileTo"
        />
        <AppMenuNewItem
          icon="fa-regular fa-arrow-right-from-bracket"
          label="Sign out"
          danger
          :command="() => authStore.signOut()"
        />
      </template>
    </AppMenuNew>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'

const authStore = useAuthStore()
const userStore = useUserStore()
const route = useRoute()

const collapsed = inject<Ref<boolean>>('navCollapsed', ref(false))

// Name + initials via the shared user utils (same as UsersTable / SystemAdmin);
// fall back to a neutral label/glyph while the user record is still loading.
const displayName = computed(() =>
  userStore.user ? getUserDisplayName(userStore.user) : 'Account',
)

const initials = computed(() =>
  userStore.user ? getUserInitials(userStore.user) : '?',
)

// Show the profile image when the user has one, else fall back to initials
// (same pattern as UserProfile / UsersTable).
const avatarUrl = computed(() => userStore.user?.avatarUrl ?? null)

// "View profile" opens the current user's own profile screen, resolving to the
// deepest domain in scope. The org/system-admin profile surfaces are super-only,
// so never send a non-super into a super-gated route:
//
//   1. Inside a workspace → workspace-domain profile (reachable by everyone).
//   2. Otherwise (super-only surfaces): super → org profile when an org slug is
//      in scope, else system-admin profile.
//
// A non-super outside a workspace has no reachable profile → return null and let
// `v-if="profileTo"` hide the item (rather than inventing an arbitrary target).
// Today this can't happen — the account button only lives in the settings navs,
// and a non-super only ever sees it in workspace settings — but the guard keeps
// the item honest if the button is ever placed somewhere without that context.
const profileTo = computed(() => {
  const userId = userStore.user?.id
  if (!userId) return null

  const organizationSlug = route.params.organizationSlug
  const workspaceId = route.params.workspaceId

  if (
    typeof organizationSlug === 'string' &&
    organizationSlug &&
    typeof workspaceId === 'string' &&
    workspaceId
  ) {
    return {
      name: 'organizationSlug-workspaceId-settings-user-userId',
      params: { organizationSlug, workspaceId, userId },
    }
  }

  if (!userStore.isSuperUser) return null

  if (typeof organizationSlug === 'string' && organizationSlug) {
    return {
      name: 'organizationSlug-settings-user-userId',
      params: { organizationSlug, userId },
    }
  }

  return { name: 'system-admin-user-userId', params: { userId } }
})
</script>
